/**
 * ZenMarkup Privacy Analytics — Widgets module.
 *
 * Device breakdown and country list table renderers.
 */

import { setWidgetContent, emptyState } from './table-builder.js';
import { build as listBuild } from './list.js';
import { formatDeviceLabel } from './utils.js';

/**
 * Render device breakdown table.
 *
 * @param {Object} data
 */
function renderDevices(data) {
	const devices = data.devices || [];

	if (!devices.length) {
		setWidgetContent('devices', emptyState('No device data yet.'));
		return;
	}

	const rows = devices.map(function (d) {
		return { name: formatDeviceLabel(d.device_type), count: d.visitors };
	});

	setWidgetContent('devices', listBuild('Device', 'Visitors', rows));
}

/**
 * Render country list table.
 *
 * @param {Object} data
 */
function renderCountries(data) {
	const countries = data.countries || [];

	if (!countries.length) {
		setWidgetContent('countries', emptyState('No country data yet.'));
		return;
	}

	const rows = countries.map(function (c) {
		return { name: c.country, count: c.visitors };
	});

	const list = listBuild('Country', 'Visitors', rows);
	setWidgetContent('countries', list);
}

/**
 * Build cities table from an array of { city, visitors }.
 *
 * @param {Array} cities
 */
function buildCitiesTable(cities) {
	if (!cities || !cities.length) {
		setWidgetContent('cities', emptyState('No city data yet.'));
		return;
	}

	const rows = cities.map(function (c) {
		return { name: c.city, count: c.visitors };
	});

	setWidgetContent('cities', listBuild('City', 'Visitors', rows));
}

/**
 * Render cities table (top 5 from first country).
 *
 * @param {Object} data  Full stats response data.
 */
function renderCities(data) {
	buildCitiesTable(data.cities || []);
}

/** Human-readable labels for exit reasons. */
const EXIT_LABELS = {
	left_fast:     'Left fast',
	single_page:   'Single page visit',
	short_session:  'Short session',
	normal_exit:   'Normal exit'
};

/** Subtle hints shown after the exit reason label. */
const EXIT_HINTS = {
	left_fast:     '< 3s',
	short_session: '3–10s'
};

/**
 * Render Exit Intelligence widgets (exit reasons + top exit pages).
 *
 * @param {Object} data  Full stats response data.
 */
function renderExitIntelligence(data) {
	const ei = data.exit_intelligence || {};

	const reasons = ei.exit_reasons || [];
	if (!reasons.length) {
		setWidgetContent('exit-reasons', emptyState('No exit data yet.'));
	} else {
		const rows = reasons.map(function (r) {
			return {
				name: EXIT_LABELS[r.reason] || r.reason,
				hint: EXIT_HINTS[r.reason] || '',
				count: r.percentage.toFixed(1) + '%'
			};
		});
		setWidgetContent('exit-reasons', listBuild('Reason', 'Share', rows));
	}

	const pages = ei.top_exit_pages || [];
	if (!pages.length) {
		setWidgetContent('exit-pages', emptyState('No exit page data yet.'));
	} else {
		const rows = pages.map(function (p) {
			return { name: p.url, count: p.count };
		});
		setWidgetContent('exit-pages', listBuild('Page', 'Exits', rows));
	}
}

/**
 * Render bot summary section (Bots tab).
 *
 * @param {Object} data  Full stats response data.
 */
function renderBots(data) {
	const bot = data.bot_summary || {};

	const pages = bot.top_pages || [];
	if (!pages.length) {
		setWidgetContent('bot-pages', emptyState('No bot traffic yet.'));
	} else {
		const pageRows = pages.map(function (p) {
			return { name: p.url, count: p.count };
		});
		setWidgetContent('bot-pages', listBuild('Page', 'Hits', pageRows));
	}

	const countries = bot.top_countries || [];
	if (!countries.length) {
		setWidgetContent('bot-countries', emptyState('No bot country data yet.'));
	} else {
		const countryRows = countries.map(function (c) {
			return { name: c.country, count: c.count };
		});
		setWidgetContent('bot-countries', listBuild('Country', 'Hits', countryRows));
	}
}

/**
 * Render all widget tables from API data.
 *
 * @param {Object} data  Response from API fetchStats().
 */
export function render(data) {
	renderDevices(data);
	renderCountries(data);
	renderCities(data);
	renderBots(data);
	renderExitIntelligence(data);
}
